import React, { useRef, SyntheticEvent, useCallback, useEffect } from 'react';
import { Cell, Row, ColumnInstance } from 'react-table';
import { gql, useQuery, useLazyQuery } from '@apollo/client';

import {
    Button,
    IconMusicNotePlus,
    IconMusicFile,
    IconPlaylist1,
    MusicFileProgressCircle,
} from '@trqk/components';
import {
    constants,
    getFileInputAccept,
    uploadTrackTaskIsActive,
    getUseUploadTrackTaskId,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { useUploadTracks } from '../../../../../../hooks';

const GET_TRQK_USER = gql`
    query getTrqkUser($id: ID!) {
        getTrqkUser(id: $id) {
            id
            subscription_detection_tracks
        }
    }
`;

const { GET_SESSION_CURRENT_USER } = queryStrings;
const { SUPPORTED_AUDIO_FILE_TYPES } = constants;
const audioFileInputAccept = getFileInputAccept(SUPPORTED_AUDIO_FILE_TYPES);

interface AudioStatusColumn extends ColumnInstance<WorksTableRow> {
    totalDetections: number;
    updateMyData: TableDataUpdater;
}

interface Props {
    column: AudioStatusColumn;
    cell: Cell<WorksTableRow>;
    row: Row<WorksTableRow>;
}

/**
 * When there is an upload in progress, displays a circular progress bar.
 * When there is not an upload in progress, displays either an icon or an upload button, based on
 * the track's status, which is one of the following:
 * - a matched track: a statement track with an audio file (renders an icon)
 * - an unmatched track: an audio file that is not associated with a statement (renders an icon)
 * - a statement track without an audio file (renders a button that enables audio upload)
 * @param props props supplied by the columns set up in TracksTable
 */
export const AudioStatusCell = (props: Props): JSX.Element => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { column, cell, row } = props;
    const { updateMyData, totalDetections } = column;
    const trqkAudioId = cell?.value?.trqkAudioId;
    const trqkSongId = cell?.value?.trqkSongId;
    const workIds = cell?.value?.workIds ?? null;
    const percentShares = cell?.value?.percentShares ?? null;
    const progress = cell?.value?.progress ?? null;
    const uploadingFilename = cell?.value?.uploading_filename ?? null;
    const title = row?.values?.track?.title ?? 'track';
    const { handleFileForTrack } = useUploadTracks();
    const taskId = getUseUploadTrackTaskId(trqkSongId ?? '_unknown_song_id_');

    const addFile = (): void => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;

    const [getTrqkUser, getTrqkUserQuery] = useLazyQuery<{
        getTrqkUser: TrqkUser;
    }>(GET_TRQK_USER);

    useEffect(() => {
        if (userId !== null) {
            getTrqkUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [userId]);

    const subscriptionDetectionTracks =
        getTrqkUserQuery?.data?.getTrqkUser?.subscription_detection_tracks ?? 0;

    const isMatchedTrack = trqkAudioId !== null && trqkSongId !== null;
    const isUnmatchedTrack = trqkAudioId !== null && trqkSongId === null;
    const isStatementTrackWithoutAudio =
        trqkAudioId === null && trqkSongId !== null;

    const handleFile = useCallback(
        (file: File) => {
            handleFileForTrack({
                file,
                percentShares,
                taskId,
                title,
                trqkSongId,
                onSuccess: (task: UploadTrackTask): void => {
                    const newTrqkAudioId = task?.output?.trqkAudio?.id ?? null;

                    if (newTrqkAudioId) {
                        updateMyData(trqkSongId, 'audio_id', newTrqkAudioId);

                        if (totalDetections < subscriptionDetectionTracks) {
                            updateMyData(
                                trqkSongId,
                                'detection_status',
                                'PENDING' as DetectionStatus,
                            );
                        }
                    }
                },
                onTaskUpdate: (task: UploadTrackTask): void => {
                    let nextProgress: Maybe<number> = task?.progress ?? null;

                    if (!uploadTrackTaskIsActive(task)) {
                        nextProgress = null;
                    }

                    if (nextProgress !== progress) {
                        updateMyData(trqkSongId, 'progress', nextProgress);
                    }

                    const nextUploadingFilename: Maybe<string> =
                        task?.input?.filename ?? null;

                    if (uploadingFilename !== nextUploadingFilename) {
                        updateMyData(
                            trqkSongId,
                            'uploading_filename',
                            nextUploadingFilename,
                        );
                    }
                },
                workIds,
            });
        },
        [updateMyData, progress],
    );

    if (progress !== null && progress < 100) {
        return (
            <div className="flex justify-center">
                <MusicFileProgressCircle progress={progress} />
            </div>
        );
    }

    return (
        <div className="flex justify-center">
            {/* If this is a statement track without audio, show the upload button */}
            {isStatementTrackWithoutAudio && (
                <Button
                    addClasses="cursor-pointer"
                    color="tertiary-light-bg"
                    padding="none"
                    handleClick={addFile}
                    autoWidth={true}
                >
                    <IconMusicNotePlus
                        fillColor="primary-500"
                        addClasses="m-1"
                    />
                    <input
                        accept={audioFileInputAccept}
                        className="visually-hidden"
                        id={`audio-status-cell-file-input-${trqkSongId}`}
                        onChange={(e: SyntheticEvent): void => {
                            e.preventDefault();

                            if (fileInputRef.current) {
                                const files = Array.from(
                                    fileInputRef.current?.files ?? [],
                                );

                                const file = files[0];

                                if (file) {
                                    handleFile(file);
                                }

                                // Reset the input's current value so it can be used again.
                                fileInputRef.current.value = '';
                            }
                        }}
                        ref={fileInputRef}
                        type="file"
                    />
                </Button>
            )}

            {/* If this is a matched track, show the appropriate icon */}
            {isMatchedTrack && <IconPlaylist1 fillColor="primary-500" />}

            {/* If this is an unmatched track, show the appropriate icon */}
            {isUnmatchedTrack && (
                <IconMusicFile fillColor="primary-500" width={30} />
            )}
        </div>
    );
};
